import React, { useState, useRef } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCircleNotch } from "@fortawesome/pro-light-svg-icons";
import type { Theme } from "@material-ui/core";
import {
    makeStyles,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    Collapse,
    Link,
} from "@material-ui/core";

import { useTranslation } from "react-i18next";
import { TransitionGroup, CSSTransition } from "react-transition-group";

import type { SiteScheduleException, ISiteSlim } from "App/Modules/Models";

import { formatDateTime, IconLabel } from "App/ReactComponents/Shared";
import { analysisPartiallyEnabled, analysisEnabled, analysisDisabled } from "../../../Img/Icons";

const animationDuration = 1000;

const useStyles = makeStyles((theme: Theme) => ({
    animateTableRow: {
        transform: "none",
        "&-enter": {
            transform: "scale(0)",
        },
        "&-enter-active": {
            overflow: "hidden",
            transform: "none",
            transition: theme.transitions.create("transform", {
                duration: animationDuration,
            }),
        },
        "&-exit": {
            maxHeight: 48,
            overflow: "hidden",
            transform: "none",
            transition: `${theme.transitions.create("transform", {
                duration: animationDuration,
            })}, ${theme.transitions.create("max-height", {
                delay: animationDuration,
                duration: animationDuration,
            })}`,
        },
        "&-exit-active": {
            transform: "scale(0)",
            maxHeight: 0,
        },
    },
    nowrap: {
        textOverflow: "ellipsis",
        overflow: "hidden",
        whiteSpace: "nowrap",
        maxWidth: 300,
    },
    table: {
        "& .MuiTableCell-body": {
            borderBottom: "none",
            verticalAlign: "top",
        },
        borderCollapse: "separate",
        borderSpacing: "0 12px",
    },
    configGroupsInfo: {
        marginBottom: "4px",
    },
    subInfo: {
        fontSize: "12px",
        opacity: 0.8,
    },
}));

type ScheduleExceptionsTableProps = {
    analysisEnabledCaption: string;
    onRemove?: (exception: SiteScheduleException) => void;
    removeButtonCaption?: string;
    scheduleExceptions: SiteScheduleException[];
    site: ISiteSlim;
    sortOrder: string;
};

export const ScheduleExceptionsTable = (props: ScheduleExceptionsTableProps) => {
    const { t } = useTranslation();
    const classes = useStyles();

    const [clicked, setClicked] = useState([] as string[]);

    const handleRemove = (ex: SiteScheduleException) => {
        setClicked([ex.id, ...clicked]);
        props.onRemove(ex);
    };

    const expandTableRowRef = useRef<HTMLTableRowElement>(null);

    return (
        <>
            <Collapse in={props.scheduleExceptions?.length === 0}>
                {t("Entities.Schedule.NoScheduleExceptions")}
            </Collapse>
            <Collapse in={!!props.scheduleExceptions && props.scheduleExceptions.length !== 0}>
                <Table size="small" className={classes.table}>
                    <TableHead>
                        <TableRow>
                            <TableCell style={{ width: "180px" }}>{props.analysisEnabledCaption}</TableCell>
                            <TableCell style={{ width: "200px" }}>{t("Entities.Schedule.StartTime")}</TableCell>
                            <TableCell style={{ width: "200px" }}>{t("Entities.Schedule.StopTime")}</TableCell>
                            <TableCell style={{ width: "300px" }}>{t("Entities.ScheduleException.Info")}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!!props.scheduleExceptions && (
                            <TransitionGroup component={null}>
                                {props.scheduleExceptions
                                    ?.sort((a, b) => {
                                        if (props.sortOrder === "desc") {
                                            return b.dtStart.diff(a.dtStart);
                                        }
                                        if (props.sortOrder === "asc") {
                                            return a.dtStart.diff(b.dtStart);
                                        }
                                        return 0;
                                    })
                                    .map((ex) => {
                                        const appliesTo = ex.isFullSiteException
                                            ? t("Entities.ScheduleException.AppliesToFullSite")
                                            : t("Entities.ScheduleException.AppliesToConfigurationGroupsFormat", {
                                                  configurationGroups: ex.configurationGroups
                                                      ?.map(
                                                          (cg) =>
                                                              cg.name ||
                                                              t("Entities.Default.DefaultDetectionConfiguration", cg),
                                                      )
                                                      ?.join(", "),
                                              });
                                        const creator = ex.users?.find(
                                            (user) =>
                                                user.status === "created" && user.exceptionRole === "loggedInUser",
                                        );
                                        const updater = ex.users?.find(
                                            (user) =>
                                                user.status === "updated" && user.exceptionRole === "loggedInUser",
                                        );
                                        const requestingCreator = ex.users?.find(
                                            (user) =>
                                                user.status === "created" && user.exceptionRole === "requestingUser",
                                        );
                                        const requestingUpdater = ex.users?.find(
                                            (user) =>
                                                user.status === "updated" && user.exceptionRole === "requestingUser",
                                        );

                                        return (
                                            <CSSTransition<HTMLTableRowElement>
                                                key={ex.id}
                                                timeout={{
                                                    enter: animationDuration,
                                                    exit: animationDuration,
                                                }}
                                                nodeRef={expandTableRowRef}
                                                classNames={classes.animateTableRow}
                                            >
                                                <TableRow key={ex.id} ref={expandTableRowRef}>
                                                    <TableCell>
                                                        {
                                                            // eslint-disable-next-line no-nested-ternary
                                                            !ex.isFullSiteException ? (
                                                                <IconLabel startIcon={analysisPartiallyEnabled}>
                                                                    {t("Entities.ScheduleException.PartiallyEnabled")}
                                                                </IconLabel>
                                                            ) : ex.isOnType ? (
                                                                <IconLabel startIcon={analysisEnabled}>
                                                                    {t("Entities.ScheduleException.Enabled")}
                                                                </IconLabel>
                                                            ) : (
                                                                <IconLabel startIcon={analysisDisabled}>
                                                                    {t("Entities.ScheduleException.Disabled")}
                                                                </IconLabel>
                                                            )
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDateTime(ex.dtStart, props.site.timeZone)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatDateTime(ex.dtStop, props.site.timeZone)}
                                                    </TableCell>
                                                    <TableCell
                                                        size="medium"
                                                        {...(ex.isFullSiteException
                                                            ? {}
                                                            : {
                                                                  title: appliesTo,
                                                              })}
                                                    >
                                                        <div className={classes.configGroupsInfo}>{appliesTo}</div>

                                                        {ex.users?.length > 0 ? (
                                                            <React.Fragment>
                                                                {!!creator ? (
                                                                    <div
                                                                        className={classes.subInfo}
                                                                        key={`added_{${ex.id}}`}
                                                                    >
                                                                        {`${t("Entities.ScheduleException.AddedBy")} `}
                                                                        <Link
                                                                            href={`/#!/partners/${props.site.partner.id}/users/${creator.id}`}
                                                                        >
                                                                            {creator.name}
                                                                        </Link>
                                                                        {!!requestingCreator ? (
                                                                            <React.Fragment>
                                                                                {`, ${t(
                                                                                    "Entities.ScheduleException.RequestedBy",
                                                                                )} `}
                                                                                <Link
                                                                                    href={`/#!/partners/${props.site.partner.id}/users/${requestingCreator.id}`}
                                                                                >
                                                                                    {requestingCreator.name}
                                                                                </Link>
                                                                            </React.Fragment>
                                                                        ) : null}
                                                                    </div>
                                                                ) : null}
                                                                {!!updater ? (
                                                                    <div
                                                                        className={classes.subInfo}
                                                                        key={`updated_{${ex.id}}`}
                                                                    >
                                                                        {`${t(
                                                                            "Entities.ScheduleException.LastUpdatedBy",
                                                                        )} `}
                                                                        <Link
                                                                            href={`/#!/partners/${props.site.partner.id}/users/${updater.id}`}
                                                                        >
                                                                            {updater.name}
                                                                        </Link>
                                                                        {!!requestingUpdater ? (
                                                                            <React.Fragment>
                                                                                {`, ${t(
                                                                                    "Entities.ScheduleException.RequestedBy",
                                                                                )} `}
                                                                                <Link
                                                                                    href={`/#!/partners/${props.site.partner.id}/users/${requestingUpdater.id}`}
                                                                                >
                                                                                    {requestingUpdater.name}
                                                                                </Link>
                                                                            </React.Fragment>
                                                                        ) : null}
                                                                    </div>
                                                                ) : null}
                                                            </React.Fragment>
                                                        ) : (
                                                            <div className={classes.subInfo} key={ex.id}>
                                                                {t(
                                                                    "Entities.ScheduleException.UserInformationNotFound",
                                                                )}
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {!!props.onRemove && (
                                                            <Button
                                                                onClick={() => handleRemove(ex)}
                                                                startIcon={
                                                                    clicked.includes(ex.id) ? (
                                                                        <FontAwesomeIcon
                                                                            icon={faCircleNotch}
                                                                            fixedWidth
                                                                            spin
                                                                        />
                                                                    ) : (
                                                                        <FontAwesomeIcon icon={faTimes} fixedWidth />
                                                                    )
                                                                }
                                                            >
                                                                {props.removeButtonCaption}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            </CSSTransition>
                                        );
                                    })}
                            </TransitionGroup>
                        )}
                    </TableBody>
                </Table>
            </Collapse>
        </>
    );
};
